<template>
  <BaseCard
    class="live-item"
    :data="formatCard(card)"
    :link="
      judgeStatus(card) !== LiveStatus.AFTER && card.jump_url
        ? card.jump_url
        : {
            name: PageKey.ACADEMY_DETAIL,
            params: {
              collection: `${category.outer_id}-${category.id}`,
              id: `${category.id}-${card.id}`,
            },
          }
    "
  >
    <template #title>
      <template v-if="cardName(card.name).length > 1">
        <span class="main-title font-[600]">
          {{ cardName(card.name)[0] }}
        </span>
        <span class="sub-title"> | {{ cardName(card.name)[1] }} </span>
      </template>
      <template v-else>
        {{ card.name }}
      </template>
    </template>
    <template #other>
      <div class="live-time flex justify-between items-center">
        <!-- status -->
        <span :class="statusClass(judgeStatus(card))">
          <iconpark-icon v-if="judgeStatus(card) === LiveStatus.BEFORE" name="clock" :size="12" />
          {{ judgeStatus(card) }}
        </span>
        <!-- time -->
        <span class="time">
          {{ startDate(card.start_time?.toString()) }}
        </span>
      </div>
      <nuxt-link class="go-replay w-full flex items-center justify-center cursor-pointer">
        <!-- 预约直播 -->
        <template v-if="judgeStatus(card) === LiveStatus.BEFORE">
          <iconpark-icon name="calendar-check" size="16" />
          <span>{{ $t('yu-yue-zhi-bo') }}</span>
        </template>
        <!-- 进入直播间 -->
        <template v-if="judgeStatus(card) === LiveStatus.NOW">
          <iconpark-icon name="arrow-right-square-contained" size="16" />
          <span>{{ $t('jin-ru-zhi-bo-jian') }}</span>
        </template>
        <!-- 看回放 -->
        <template v-else-if="judgeStatus(card) === LiveStatus.AFTER">
          <iconpark-icon name="play-01" :size="16" />
          <span>{{ $t('kan-hui-fang') }}</span>
        </template>
      </nuxt-link>
    </template>
  </BaseCard>
</template>

<script lang="ts" setup>
import type { MagicSchoolDetail, IHelpCategory } from '~/api/module/help-center';
import BaseCard, { type IBaseCardData } from './baseCard.vue';
import { useTime } from '~/hooks/useTime';
import { PageKey } from '~/constants/page';

const props = defineProps<{
  card: MagicSchoolDetail;
  category: IHelpCategory;
}>();

const { $t } = useNuxtApp();
const { timeFormat, $dayjs } = useTime();

const formatCard = (card: MagicSchoolDetail) => {
  return {
    title: card.name,
    cover_image_url: card.cover_url,
    ...card,
  } satisfies IBaseCardData;
};

const startDate = (date: string | undefined) => {
  if (!date) return '';
  return timeFormat(date, 'YYYY-MM-DD HH:mm');
};

const cardName = (name: string) => name.split('|');

const LiveStatus = {
  BEFORE: $t('wei-kai-shi'),
  AFTER: $t('yi-jie-shu'),
  NOW: $t('zhi-bo-zhong'),
};

const judgeStatus = (card: MagicSchoolDetail) => {
  if ($dayjs().isBefore($dayjs(card.start_time))) {
    return LiveStatus.BEFORE;
  }
  if ($dayjs().isAfter($dayjs(card.end_time))) {
    return LiveStatus.AFTER;
  }
  return LiveStatus.NOW;
};

const statusClass = (status: string) => {
  return {
    'live-status': true,
    before: status === LiveStatus.BEFORE,
    after: status === LiveStatus.AFTER,
    now: status === LiveStatus.NOW,
  };
};
</script>

<style lang="scss" scoped>
.live-item {
  cursor: initial;
  :deep(.arrow) {
    display: none !important;
  }
  &:hover {
    .go-replay {
      background-color: var(--primary-color);
      color: var(--text-0);
      border-radius: 8px;
    }
  }
}

@keyframes breathe {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(2);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.live-time {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-1);
  .live-status {
    border-radius: 4px;
    padding: 4px 8px;
    display: flex;
    align-items: center;
    justify-content: center;

    iconpark-icon {
      margin-right: 4px;
    }

    &.before {
      color: var(--purple-6);
      background-color: var(--purple-1);
    }
    &.after {
      color: var(--back-8);
      background-color: var(--back-3);
      // 呼吸灯圆点
      &::before {
        content: '';
        display: block;
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background-color: var(--back-8);
        margin-right: 4px;
      }
    }
    &.now {
      color: var(--success-7);
      background-color: var(--success-1);

      // 呼吸灯圆点
      &::before {
        content: '';
        display: block;
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background-color: var(--success-7);
        margin-right: 4px;
        animation: breathe 1.5s infinite;
      }
    }
  }
}
.go-replay {
  margin-top: 12px;
  height: 42px;
  background-color: var(--back-2);
  color: var(--primary-color);
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  iconpark-icon {
    margin-right: 8px;
  }
}
</style>
